import { Injectable } from '@angular/core';
import { db, OfflineDb } from './db';

/** Lo mínimo para reconocer una venta: id del servidor o clave local. */
export interface RefDeTirilla {
  idOrder?: number | null;
  clave?: string | null;
}

/** Fila guardada: la venta sin tirilla tal como la lleva `ImpresionService`. */
export interface TirillaPendiente<V extends RefDeTirilla = RefDeTirilla> {
  tenantId: string;
  /** `clave:<idempotencyKey>` o `orden:<idOrder>`. */
  ref: string;
  /** ISO del momento en que se anotó; sirve para cargar solo las de hoy. */
  anotadaEn: string;
  venta: V;
}

/**
 * F2.5 — La lista «sin tirilla» sobrevive a recargar la página.
 *
 * `ImpresionService.pendientes` vivía solo en memoria y se repoblaba desde el
 * historial del servidor, que solo conoce ventas con `idOrder`: una venta hecha
 * SIN RED y no impresa desaparecía de la lista al recargar, y con ella la única
 * copia de lo que había que imprimir (nombre de cada producto, efectivo y
 * cambio). Aquí se guarda por negocio.
 */
@Injectable({ providedIn: 'root' })
export class TirillasPendientesStore {
  private base: OfflineDb = db;

  /** Solo para pruebas: la misma lógica sobre otra base (fake-indexeddb). */
  static sobre(base: OfflineDb): TirillasPendientesStore {
    const store = new TirillasPendientesStore();
    store.base = base;
    return store;
  }

  static ref(v: RefDeTirilla): string | null {
    if (v.clave) return `clave:${v.clave}`;
    if (v.idOrder) return `orden:${v.idOrder}`;
    return null;
  }

  async guardar<V extends RefDeTirilla>(tenantId: string, venta: V): Promise<void> {
    const ref = TirillasPendientesStore.ref(venta);
    if (!ref) return;
    await this.base.transaction('rw', this.base.tirillasPendientes, async () => {
      await this.quitarDentro(tenantId, venta);
      await this.base.tirillasPendientes.put({ tenantId, ref, anotadaEn: new Date().toISOString(), venta });
    });
  }

  async quitar(tenantId: string, venta: RefDeTirilla): Promise<void> {
    await this.base.transaction('rw', this.base.tirillasPendientes, () => this.quitarDentro(tenantId, venta));
  }

  /** Las de `tenantId` anotadas hoy (hora local del equipo), más recientes primero. */
  async deHoy<V extends RefDeTirilla>(tenantId: string): Promise<V[]> {
    const hoy = new Date().toDateString();
    const filas = (await this.base.tirillasPendientes.where('tenantId').equals(tenantId).toArray()) as TirillaPendiente<V>[];
    return filas
      .filter((f) => new Date(f.anotadaEn).toDateString() === hoy)
      .sort((a, b) => b.anotadaEn.localeCompare(a.anotadaEn))
      .map((f) => f.venta);
  }

  /** La misma venta puede estar guardada por su clave y conocerse después por su id: se quitan las dos. */
  private async quitarDentro(tenantId: string, venta: RefDeTirilla): Promise<void> {
    const filas = await this.base.tirillasPendientes.where('tenantId').equals(tenantId).toArray();
    const mismas = filas.filter(
      (f) =>
        (!!venta.clave && f.venta.clave === venta.clave) ||
        (!!venta.idOrder && f.venta.idOrder === venta.idOrder),
    );
    await this.base.tirillasPendientes.bulkDelete(mismas.map((f) => [f.tenantId, f.ref] as [string, string]));
  }
}
